import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { cargas, recorrido } from "../services/fueguino";
import { ORDEN_ESTADOS, panel, resumen } from "../shared/fueguino";
import Loading from "./Loading";

import "./css/fueguino.css";

/* El monitor de cargas de Fueguino: todas las encomiendas, juntas.
 *
 * ── LA PREGUNTA QUE CONTESTA ───────────────────────────────────────────────
 *
 * «¿El paquete 43 de Pepito en qué camión va, y dónde está ese camión?» era
 * imposible de contestar rápido, y ése fue el motivo por el que arrancó todo
 * el sector. En el mostrador se contesta de a una guía, escaneando. Acá se
 * contesta mirando: la lista entera, ordenada por la que necesita que alguien
 * haga algo hoy.
 *
 * ── ES UNA PANTALLA DE MIRAR ───────────────────────────────────────────────
 *
 * No se carga nada desde acá. Una guía nace donde se pesa el bulto, que es el
 * mostrador, en la PWA `encomiendas.fibot.ar` —y se escanea ahí porque allá el
 * internet se corta y la cola offline es el camino, no el plan B—. Duplicar la
 * carga en el hub obligaría a adivinar en cuál de los dos lados se anota.
 *
 * ── POR QUÉ «PARCIAL» ESTÁ ARRIBA DE TODO ──────────────────────────────────
 *
 * Una guía con 8 bultos en el camión y 2 en el depósito no está «en viaje»:
 * está partida, y nadie se entera hasta que el cliente reclama los 2 que
 * quedaron en Buenos Aires. El orden de la tabla no es cronológico a propósito
 * —lo cronológico lo contesta cualquier planilla—: arriba va lo que está mal.
 */

const cuando = (iso) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

/* El estado se dice con palabras y con color. Sin el color hay que leer seis
 * filas para encontrar la que importa; sin la palabra, el que no conoce el
 * código de colores no entiende nada. Van los dos. */
const TONO = {
  parcial: "alerta",
  "en destino": "aviso",
  "en viaje": "bien",
  "en depósito": "neutro",
  finalizada: "tenue",
  "sin bultos": "tenue",
};

/* El rótulo que se muestra usa el VOCABULARIO DE CRISTAL, a propósito: la
 * gente de Fueguino viene de ahí y «Recibido en Destino» o «Entregado
 * Conforme» son las palabras que ya tiene en la cabeza. El estado interno
 * (`en destino`, `finalizada`) sigue siendo el que deriva la lógica —esto es
 * sólo la etiqueta—, así que `shared/fueguino` no se entera y sus tests no se
 * tocan. */
const ETIQUETA_ESTADO = {
  parcial: "Parcial",
  "en destino": "Recibido en Destino",
  "en viaje": "En viaje",
  "en depósito": "En Depósito",
  finalizada: "Entregado Conforme",
  "sin bultos": "Sin bultos",
};
const rotulo = (estado) => ETIQUETA_ESTADO[estado] || estado;

const FueguinoCargas = () => {
  /* Igual que Tarifas y AREF: quien no puede ver encomiendas se encuentra con
   * la puerta cerrada. Sin esto la consulta volvía vacía —la base no le deja
   * ver ninguna guía— y la pantalla decía «Todavía no hay ninguna guía», que
   * es contestar «no hay» donde la respuesta es «no podés pasar». */
  const { permisos = null } = useOutletContext() || {};
  const cerrada = permisos !== null && !permisos.includes("ver.encomiendas");

  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const [eventos, setEventos] = useState({});

  const traer = useCallback(async () => {
    try {
      setDatos(await cargas());
    } catch (e) {
      setError(e.message || "No se pudieron leer las encomiendas");
    }
  }, []);

  useEffect(() => {
    if (!cerrada) traer();
  }, [traer, cerrada]);

  const filas = useMemo(
    () => (datos ? panel(datos.guias, datos.bultosPorGuia) : []),
    [datos],
  );
  const total = useMemo(() => resumen(filas), [filas]);
  const visibles = filtro ? filas.filter((f) => f.estado === filtro) : filas;

  /* El recorrido se pide al abrir la guía y queda guardado: son la mayoría de
   * las filas del sector y casi ninguna se mira. Volver a cerrar y abrir no
   * pide de nuevo. */
  const abrir = async (guia) => {
    if (abierta === guia.id) return setAbierta(null);
    setAbierta(guia.id);
    if (eventos[guia.id]) return;
    try {
      const r = await recorrido(guia.id);
      setEventos((prev) => ({ ...prev, [guia.id]: r }));
    } catch (e) {
      setEventos((prev) => ({ ...prev, [guia.id]: [] }));
      setError(e.message || "No se pudo leer el recorrido");
    }
  };

  if (cerrada) return <PuertaCerrada />;
  if (error && !datos) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!datos) return <Loading />;

  return (
    <div className="docs fu-cargas">
      <div className="tk-filtros">
        <button
          type="button"
          className={`link-button${filtro === null ? " activo" : ""}`}
          onClick={() => setFiltro(null)}
        >
          Todas ({total.guias})
        </button>
        {ORDEN_ESTADOS.filter((e) => total.por[e]).map((e) => (
          <button
            key={e}
            type="button"
            className={`link-button${filtro === e ? " activo" : ""}`}
            onClick={() => setFiltro(filtro === e ? null : e)}
          >
            {rotulo(e)} ({total.por[e]})
          </button>
        ))}
      </div>

      {error && <p className="panel-aviso suave">{error}</p>}

      {filas.length === 0 && (
        <p className="panel-aviso suave">
          Todavía no hay ninguna guía.
        </p>
      )}

      {filas.length > 0 && (
        <div className="fu-totales">
          <span>
            <b>{total.guias}</b>
            {total.guias === 1 ? "guía" : "guías"}
          </span>
          <span className={total.por["parcial"] ? "fu-total-alerta" : ""}>
            <b>{total.enCamino}</b>
            en camino
          </span>
          <span>
            <b>{total.bultos}</b>
            bultos
          </span>
        </div>
      )}

      {filas.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Guía</th>
                <th>Recorrido</th>
                <th>Quién</th>
                <th>Bultos</th>
                <th>Estado</th>
                <th>Cuándo</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (
                <Fragment key={f.id}>
                  <tr onClick={() => abrir(f)} style={{ cursor: "pointer" }}>
                    <td>
                      <b>{f.id}</b>
                      {f.tipo && <span className="sub">{f.tipo}</span>}
                    </td>
                    <td>
                      {f.origen || <span className="sin-dato">sin origen</span>}
                      <span className="sub">
                        → {f.destino || "sin destino"}
                      </span>
                    </td>
                    <td>
                      {f.remitente || <span className="sin-dato">sin remitente</span>}
                      <span className="sub">
                        para {f.destinatario || "sin destinatario"}
                      </span>
                    </td>
                    <td>
                      {f.avance.listos}/{f.avance.total}
                      {/* `bultos_total` es lo que el mostrador DIJO que eran;
                          las filas de `enc_bultos` son las que existen. Si no
                          coinciden hay etiquetas sin imprimir, y eso se dice
                          en vez de mostrar el número lindo. */}
                      {f.bultos_total != null &&
                        f.bultos_total !== f.avance.total && (
                          <span className="sub">
                            declaró {f.bultos_total}
                          </span>
                        )}
                    </td>
                    <td>
                      <span className={`fu-estado fu-estado--${TONO[f.estado] || "tenue"}`}>
                        {rotulo(f.estado)}
                      </span>
                    </td>
                    <td className="tenue">{cuando(f.creado_en)}</td>
                  </tr>
                  {abierta === f.id && (
                    <tr>
                      <td colSpan={6}>
                        <Recorrido eventos={eventos[f.id]} guia={f} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="docs-pie">
        Las guías se cargan en{" "}
        <a href="https://encomiendas.fibot.ar" target="_blank" rel="noreferrer">
          encomiendas.fibot.ar
        </a>.
      </p>
    </div>
  );
};

/* El recorrido de la guía: el event-log, tal cual quedó.
 *
 * La hora que se muestra es la del ESCANEO, no la del sync. Con señal mala
 * pueden ser horas distintas y la que le importa a quien pregunta «¿cuándo
 * llegó?» es la primera. Cuando la diferencia es grande se dice, porque si no
 * parece un error de carga. */
const Recorrido = ({ eventos, guia }) => {
  if (!eventos) return <p className="tenue">Buscando el recorrido…</p>;
  if (!eventos.length)
    return (
      <p className="tenue">
        La guía {guia.id} no tiene ningún evento todavía: se dio de alta pero
        nadie escaneó sus bultos.
      </p>
    );
  return (
    <ul className="recorrido">
      {eventos.map((e) => (
        <li key={e.id}>
          <b>{e.tipo}</b>
          {e.bulto_id ? ` · bulto ${e.bulto_id}` : " · toda la guía"}
          {e.interno && ` · interno ${e.interno}`}
          {e.lugar && ` · ${e.lugar}`}{" "}
          <span className="tenue">
            {cuando(e.escaneado_en || e.creado_en)}
            {e.usuario_nombre && ` · ${e.usuario_nombre}`}
          </span>
          {e.detalle && <div className="sub">{e.detalle}</div>}
        </li>
      ))}
    </ul>
  );
};

export default FueguinoCargas;
